import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

// Sentry Configuration
export const initSentry = () => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.REACT_APP_ENVIRONMENT || 'development',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: process.env.REACT_APP_ENVIRONMENT === 'production' ? 0.1 : 1.0,
      // Session Replay
      replaysSessionSampleRate: process.env.REACT_APP_ENVIRONMENT === 'production' ? 0.1 : 1.0,
      replaysOnErrorSampleRate: 1.0,
      // Release tracking
      release: process.env.REACT_APP_VERSION || '1.0.0',
      beforeSend(event) {
        // Filter out development errors in production
        if (process.env.REACT_APP_ENVIRONMENT === 'development') {
          return event;
        }
        return event;
      },
    });
  }
};

// PostHog Configuration
export const initPostHog = () => {
  if (process.env.REACT_APP_POSTHOG_KEY) {
    posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com',
      // Enable session recording
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
      // Capture pageviews automatically
      capture_pageview: true,
      // Capture performance metrics
      capture_performance: true,
      // Enable feature flags
      bootstrap: {
        featureFlags: {},
      },
      // Disable in development if needed
      disabled: process.env.REACT_APP_ENVIRONMENT === 'development' ? false : false,
    });
  }
};

// Analytics helper functions
export const analytics = {
  // Track events
  track: (eventName, properties = {}) => {
    if (typeof posthog !== 'undefined') {
      posthog.capture(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
        environment: process.env.REACT_APP_ENVIRONMENT,
      });
    }
  },

  // Identify users
  identify: (userId, traits = {}) => {
    if (typeof posthog !== 'undefined') {
      posthog.identify(userId, traits);
    }
    
    // Also set user context in Sentry
    Sentry.setUser({
      id: userId,
      ...traits,
    });
  },

  // Track page views
  page: (pageName, properties = {}) => {
    if (typeof posthog !== 'undefined') {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        page_name: pageName,
        ...properties,
      });
    }
  },

  // Set user properties
  setUserProperties: (properties) => {
    if (typeof posthog !== 'undefined') {
      posthog.people.set(properties);
    }
  },

  // Feature flags
  isFeatureEnabled: (flagKey) => {
    if (typeof posthog !== 'undefined') {
      return posthog.isFeatureEnabled(flagKey);
    }
    return false;
  },

  // Group analytics
  group: (groupType, groupKey, properties = {}) => {
    if (typeof posthog !== 'undefined') {
      posthog.group(groupType, groupKey, properties);
    }
  },

  // Reset user (for logout)
  reset: () => {
    if (typeof posthog !== 'undefined') {
      posthog.reset();
    }
    Sentry.setUser(null);
  },
};

// Hotjar helper functions
export const hotjar = {
  // Identify users in Hotjar
  identify: (userId, attributes = {}) => {
    if (window.hj) {
      window.hj('identify', userId, attributes);
    }
  },

  // Trigger events in Hotjar
  event: (eventName) => {
    if (window.hj) {
      window.hj('event', eventName);
    }
  },

  // Add tags to recordings
  tagRecording: (tags) => {
    if (window.hj) {
      tags.forEach(tag => {
        window.hj('tagRecording', [tag]);
      });
    }
  },
};

// Error tracking helper
export const trackError = (error, context = {}) => {
  // Send to Sentry
  Sentry.captureException(error, {
    tags: {
      section: context.section || 'unknown',
    },
    extra: context,
  });

  // Also track in PostHog for analytics
  analytics.track('Error Occurred', {
    error_message: error.message,
    error_stack: error.stack,
    ...context,
  });
};

// Performance tracking
export const trackPerformance = (metricName, value, unit = 'ms') => {
  // Track in PostHog
  analytics.track('Performance Metric', {
    metric_name: metricName,
    value: value,
    unit: unit,
  });

  // Send to Sentry as a measurement
  Sentry.addBreadcrumb({
    category: 'performance',
    message: `${metricName}: ${value}${unit}`,
    level: 'info',
  });
};

export default {
  initSentry,
  initPostHog,
  analytics,
  hotjar,
  trackError,
  trackPerformance,
}; 